import Konva from 'konva';
import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isPointInPolygon } from 'components/tool/Editor/AddObstructionBtn/utils';
import { RootState } from 'store';
import { setSelectedShapeId } from 'store/slices/TreeSlice';
import { selectFlattenAreaState } from 'store/slices/TreeSlice/selector';

export type ImageScale = { x: number; y: number };
export type ImagePoint = { x: number; y: number };

// Ref mirroring the latest value — lets a stable callback read fresh state, avoiding Konva listener rebinds.
export function useLatestRef<T>(value: T) {
	const ref = useRef(value);
	ref.current = value;
	return ref;
}

// Shared pre-draw guard state, as refs — one hook instead of repeating it in each of the 3 tools.
export function useFlattenAreaGuardRefs() {
	const selectedShapeId = useSelector((state: RootState) => state.treeSlice.data.selectedShapeId);
	const { flattenAreaPolygons } = useSelector(selectFlattenAreaState);
	const loadingFlattenIds = useSelector((state: RootState) => state.treeSlice.data.loadingFlattenIds);
	return {
		selectedShapeIdRef: useLatestRef(selectedShapeId),
		flattenAreaPolygonsRef: useLatestRef(flattenAreaPolygons),
		loadingFlattenIdsRef: useLatestRef(loadingFlattenIds),
	};
}

// Pinch-vs-tap bookkeeping for Circle/Free/Polygon, 
export function usePinchAwareTap(performTap: (point: ImagePoint) => void) {
	const activePointerIdRef = useRef<number | null>(null); // finger currently down
	const isAnyPointerDownRef = useRef(false); // that finger still pressed?
	const isPinchingRef = useRef(false); // 2+ fingers down?
	const pendingTapRef = useRef<ImagePoint | null>(null); // tap awaiting confirm

	// True = a 2nd, concurrent finger — caller should return.
	const isConcurrentFinger = useCallback((e: Konva.KonvaEventObject<PointerEvent>) => {
		if (isAnyPointerDownRef.current && e.evt.pointerId !== activePointerIdRef.current) return true;
		isAnyPointerDownRef.current = true;
		activePointerIdRef.current = e.evt.pointerId;
		return false;
	}, []);

	// Touch: hold for confirmation. Mouse: act now.
	const startTap = useCallback((e: Konva.KonvaEventObject<PointerEvent>, point: ImagePoint) => {
		if (e.evt.pointerType === 'touch') {
			pendingTapRef.current = point;
			return;
		}
		performTap(point);
	}, [performTap]);

	const clearPointerUp = useCallback((e: Konva.KonvaEventObject<PointerEvent>) => {
		if (e.evt.pointerId === activePointerIdRef.current) isAnyPointerDownRef.current = false;
	}, []);

	const markPinching = useCallback(() => {
		isPinchingRef.current = true;
		pendingTapRef.current = null; // a pinch, not a tap — drop it
	}, []);
	const confirmPendingTap = useCallback(() => {
		const point = pendingTapRef.current;
		if (!point) return;
		pendingTapRef.current = null;
		performTap(point);
	}, [performTap]);

	const handleTouchStart = useCallback((e: Konva.KonvaEventObject<TouchEvent>) => {
		if ((e.evt.touches?.length ?? 0) >= 2) markPinching();
	}, [markPinching]);
	const handleTouchMove = useCallback((e: Konva.KonvaEventObject<TouchEvent>) => {
		if ((e.evt.touches?.length ?? 0) >= 2) { markPinching(); return; }
		confirmPendingTap();
	}, [markPinching, confirmPendingTap]);
	const handleTouchEnd = useCallback((e: Konva.KonvaEventObject<TouchEvent>) => {
		if ((e.evt.touches?.length ?? 0) !== 0) return;
		isPinchingRef.current = false;
		confirmPendingTap();
	}, [confirmPendingTap]);

	return {
		isPinchingRef, isConcurrentFinger, startTap, clearPointerUp,
		handleTouchStart, handleTouchMove, handleTouchEnd,
	};
}

// Existing shape (if any) containing `point` — fallback for when Konva's hit-test misses a shape's node.
export const findFlattenAreaAtPoint = <T extends { coords: number[][] }>(
	point: ImagePoint,
	flattenAreaPolygons: T[]
): T | null => {
	for (const polygon of flattenAreaPolygons) {
		if (isPointInPolygon(point, polygon.coords.flat())) return polygon;
	}
	return null;
};

// True = don't start a new shape here: deselects if one was selected, else selects (or blocks, if loading) the shape under `point`.
export function shouldBlockNewShape(
	dispatch: ReturnType<typeof useDispatch>,
	point: ImagePoint,
	selectedShapeId: string | number | null,
	flattenAreaPolygons: { id: string | number; coords: number[][] }[],
	loadingFlattenIds: Record<string, boolean>
): boolean {
	if (selectedShapeId) {
		dispatch(setSelectedShapeId(null));
		return true;
	}
	const existingShape = findFlattenAreaAtPoint(point, flattenAreaPolygons);
	if (existingShape) {
		if (!loadingFlattenIds[String(existingShape.id)]) dispatch(setSelectedShapeId(existingShape.id));
		return true;
	}
	return false;
}

export const getImagePointFromEvent = (
	e: Konva.KonvaEventObject<PointerEvent>,
	baseImageScale: ImageScale
): ImagePoint | null => {
	const pos = e.target.getStage()?.getRelativePointerPosition();
	if (!pos) return null;
	return {
		x: pos.x / baseImageScale.x,
		y: pos.y / baseImageScale.y,
	};
};

export const circleToPolygonPoints = (
	cx: number,
	cy: number,
	radius: number,
	segments = 32
): number[] => {
	const pts: number[] = [];
	for (let i = 0; i < segments; i += 1) {
		const a = (i / segments) * Math.PI * 2;
		pts.push(
			cx + Math.cos(a) * radius,
			cy + Math.sin(a) * radius
		);
	}
	return pts;
};

export const getCircleFromPolygonPoints = (points: number[]) => {
	let sumX = 0;
	let sumY = 0;
	const n = points.length / 2;

	for (let i = 0; i < points.length; i += 2) {
		sumX += points[i];
		sumY += points[i + 1];
	}

	const cx = sumX / n;
	const cy = sumY / n;

	let r = 0;
	for (let i = 0; i < points.length; i += 2) {
		const dx = points[i] - cx;
		const dy = points[i + 1] - cy;
		r += Math.sqrt(dx * dx + dy * dy);
	}

	return { cx, cy, radius: r / n };
};

export const getCircleHandlePoints = (cx: number, cy: number, r: number) => ([
	{ x: cx + r, y: cy }, // 0°
	{ x: cx, y: cy - r }, // 90°
	{ x: cx - r, y: cy }, // 180°
	{ x: cx, y: cy + r }, // 270°
]);

// Clickable region for a handle — a circle of radius+padding, bigger than the visible dot.
export const getHandleHitFunc = (radius: number, padding: number) => (
	(ctx: Konva.Context, node: Konva.Shape) => {
		ctx.beginPath();
		ctx.arc(0, 0, radius + padding, 0, Math.PI * 2);
		ctx.closePath();
		ctx.fillStrokeShape(node);
	}
);
